import React from 'react'

import { SimpleGrid, Text } from '@driftt/dds'
import { KPICard, TypeFormats } from '@driftt/dds.reports'

import { getFooterInfo } from 'components/SiteConciergeSearchApp/KPICards/KPICards.helpers'
import { isNilOrEmpty } from 'utils/ramda'

import useSiteConciergeMeetingsBooked from '../Hooks/useSiteConciergeMeetingsBooked.hook'
import { useSiteConciergeMeetingsCardsMetrics } from '../Hooks/useSiteConciergeMeetingsCardsMetrics.hook'

type CardMetrics = {
  countLoads: number
  meetingAppLoads: number
  meetingButtonClicks: number
}

export const KPICards = () => {
  const {
    metrics: currentMetrics = {},
    isLoading,
    isError,
  } = useSiteConciergeMeetingsCardsMetrics()

  const { metrics: previousMetrics = {} } =
    useSiteConciergeMeetingsCardsMetrics({
      isPreviousTimePeriod: true,
    })

  const {
    meetingsBooked: currentMeetingsBooked,
    isLoading: isMeetingsBookedLoading,
    isError: isMeetingsBookedError,
  } = useSiteConciergeMeetingsBooked()

  const { meetingsBooked: previousMeetingsBooked } =
    useSiteConciergeMeetingsBooked({
      isPreviousTimePeriod: true,
    })

  const {
    countLoads: currentCountLoads,
    meetingAppLoads: currentMeetingAppLoads,
    meetingButtonClicks: currentMeetingButtonClicks,
  } = currentMetrics as CardMetrics

  const {
    meetingAppLoads: previousMeetingAppLoads,
    meetingButtonClicks: previousMeetingButtonClicks,
  } = previousMetrics as CardMetrics

  const currentDropOffRate = currentMeetingButtonClicks - currentMeetingsBooked
  const previousDropOffRate =
    previousMeetingButtonClicks - previousMeetingsBooked

  return (
    <SimpleGrid columns={4} gap={3}>
      <KPICard
        formatMetric={(value) => TypeFormats.RAW_HUMANIZED(value)}
        metric={currentMeetingAppLoads}
        previousMetric={previousMeetingAppLoads}
        isLoading={isLoading}
        hasNoData={isNilOrEmpty(currentMetrics)}
        hasError={isError}
      >
        <KPICard.Header
          title="App button impressions"
          description="How often did site visitors see the meeting app when on the page?"
        />
        <KPICard.Metric />
        <Text>
          {getFooterInfo({
            impressions: currentMeetingAppLoads,
            totalImpressions: currentCountLoads,
            label: 'page visits',
            showFooterInfo: !isError && !isLoading,
          })}
        </Text>
      </KPICard>
      <KPICard
        formatMetric={(value) => TypeFormats.RAW_HUMANIZED(value)}
        metric={currentMeetingButtonClicks}
        previousMetric={previousMeetingButtonClicks}
        isLoading={isLoading}
        hasNoData={isNilOrEmpty(currentMetrics)}
        hasError={isError}
      >
        <KPICard.Header
          title="App button clicks"
          description="How often did site visitors click on the meeting app when it was present?"
        />
        <KPICard.Metric />
        <Text>
          {getFooterInfo({
            impressions: currentMeetingButtonClicks,
            totalImpressions: currentMeetingAppLoads,
            label: 'impressions',
            showFooterInfo: !isError && !isLoading,
          })}
        </Text>
      </KPICard>
      <KPICard
        formatMetric={(value) => TypeFormats.RAW_HUMANIZED(value)}
        metric={currentMeetingsBooked}
        previousMetric={previousMeetingsBooked}
        isLoading={isMeetingsBookedLoading}
        hasNoData={isNilOrEmpty(previousMeetingsBooked)}
        hasError={isMeetingsBookedError}
      >
        <KPICard.Header
          title="Meetings booked"
          description="How many meetings were successfully booked with customers?"
        />
        <KPICard.Metric />
        <Text>
          {getFooterInfo({
            impressions: currentMeetingsBooked,
            totalImpressions: currentMeetingButtonClicks,
            label: 'sessions',
            showFooterInfo: !isMeetingsBookedError && !isMeetingsBookedLoading,
          })}
        </Text>
      </KPICard>
      <KPICard
        formatMetric={(value) => TypeFormats.RAW_HUMANIZED(value)}
        metric={currentDropOffRate}
        previousMetric={previousDropOffRate}
        isLoading={isLoading || isMeetingsBookedLoading}
        hasNoData={isNilOrEmpty(currentDropOffRate)}
        hasError={isError || isMeetingsBookedError}
      >
        <KPICard.Header
          title="Drop-off rate"
          description="How many customers did not complete a meeting booking?"
        />
        <KPICard.Metric />
        <Text>
          {getFooterInfo({
            impressions: currentDropOffRate,
            totalImpressions: currentMeetingButtonClicks,
            label: 'sessions',
            showFooterInfo:
              !isLoading &&
              !isError &&
              !isMeetingsBookedError &&
              !isMeetingsBookedLoading,
          })}
        </Text>
      </KPICard>
    </SimpleGrid>
  )
}
